import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { AuthContext } from '../../../contexts/AuthContext';
import axios from '../../../api/axios';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import UserAvatar from '../../../components/Avatar/UserAvatar';
import SidebarWithAccordion from '../../../components/Dashboard/SidebarWithAccordion';
import { createTeacherSidebarConfig } from '../../../components/Dashboard/TeacherSidebarConfig';
import TeacherOverviewTab from '../../../components/Dashboard/Teacher/TeacherOverviewTab';
import TeacherStudentsTab from '../../../components/Dashboard/Teacher/TeacherStudentsTab';
import TeacherQuestionsTab from '../../../components/Dashboard/Teacher/TeacherQuestionsTab';

function TeacherDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedCourse, logout } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [category, setCategory] = useState('Dictionary');
  const [questionNumber, setQuestionNumber] = useState('');
  const [difficulty, setDifficulty] = useState('easy');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [students, setStudents] = useState([]);
  const [overviewStats, setOverviewStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sortBy, setSortBy] = useState('questionNumber');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const [questionStudents, setQuestionStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [notebooks, setNotebooks] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectedNotebook, setSelectedNotebook] = useState(null);

  // 题目 Tab：合并为一次 effect，用 AbortController 避免 Strict Mode 或依赖变化时的重复请求
  useEffect(() => {
    if (tabValue === 1) {
      fetchStudents();
      return;
    }
    if (tabValue === 0) {
      fetchOverviewStats();
      return;
    }
    if (tabValue === 2) {
      const ctrl = new AbortController();
      fetchNotebooks(ctrl.signal);
      fetchQuestions(ctrl.signal);
      return () => ctrl.abort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, selectedCourse, selectedNotebook]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      
      if (!selectedCourse || !selectedCourse.id) {
        console.error('No course selected');
        setStudents([]);
        return;
      }
      
      const courseTemplateId = selectedCourse.id;
      const response = await axios.get('/teacher/students', {
        params: { courseTemplateId: parseInt(courseTemplateId) }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (e, student) => {
    e.stopPropagation();
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!studentToDelete) return;
    
    try {
      setDeleting(true);
      await axios.delete(`/teacher/students/${studentToDelete.id}`);
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
      await fetchStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('删除学生失败，请重试');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setStudentToDelete(null);
  };

  const fetchOverviewStats = async () => {
    try {
      setLoading(true);
      
      if (!selectedCourse || !selectedCourse.id) {
        console.error('No course selected');
        setOverviewStats(null);
        return;
      }
      
      const courseTemplateId = selectedCourse.id;
      const response = await axios.get('/teacher/stats/overview', {
        params: { courseTemplateId: parseInt(courseTemplateId) }
      });
      setOverviewStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotebooks = async (signal) => {
    try {
      if (!selectedCourse || !selectedCourse.code) {
        return;
      }
      const response = await axios.get(`/notes/course/${selectedCourse.code}`, { signal });
      const notebooksList = response.data || [];
      setNotebooks(notebooksList);
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching notebooks:', error);
      setNotebooks([]);
    }
  };

  const fetchQuestions = async (signal) => {
    try {
      setLoading(true);
      if (!selectedCourse || !selectedCourse.id) {
        console.error('No course selected');
        setQuestions([]);
        setCategories([]);
        return;
      }
      const courseTemplateId = selectedCourse.id;
      const reqConfig = { signal };
      if (selectedNotebook) {
        const questionsRes = await axios.get('/questions', {
          params: { courseTemplateId: parseInt(courseTemplateId) },
          ...reqConfig
        });
        const notebookQuestions = (questionsRes.data || []).filter(
          (q) => q.isNotebookExample && q.notebookId === selectedNotebook
        );
        setQuestions(notebookQuestions);
        setCategories([]);
      } else {
        const [questionsRes, categoriesRes] = await Promise.all([
          axios.get('/questions', {
            params: { courseTemplateId: parseInt(courseTemplateId) },
            ...reqConfig
          }),
          axios.get('/questions/categories', {
            params: { courseTemplateId: parseInt(courseTemplateId) },
            ...reqConfig
          })
        ]);
        const regularQuestions = (questionsRes.data || []).filter((q) => !q.isNotebookExample);
        setQuestions(regularQuestions);
        setCategories(categoriesRes.data || []);
      }
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching questions:', error);
      setQuestions([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.py')) {
      setSelectedFile(file);
      setUploadResult(null);
    } else {
      alert('请选择Python文件(.py)');
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || !questionNumber) {
      alert('请选择文件并输入题号');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', category);
      formData.append('questionNumber', questionNumber);
      formData.append('difficulty', difficulty);

      await axios.post('/questions/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadResult({ success: true, message: '题目上传成功！' });
      setSelectedFile(null);
      setQuestionNumber('');
      
      const fileInput = document.getElementById('upload-file-input');
      if (fileInput) fileInput.value = '';

      setTimeout(() => {
        setUploadDialogOpen(false);
        fetchOverviewStats();
      }, 1500);
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.response?.data?.error || '上传失败，请重试'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleMenuClose = () => {
    // Menu 已移除，此函数保留以备将来使用
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleViewQuestionStudents = async (e, question) => {
    e.stopPropagation();
    
    setSelectedQuestion(question);
    setStudentsDialogOpen(true);
    setLoadingStudents(true);
    setQuestionStudents([]);
    
    try {
      // Support both regular questions and notebook examples
      let questionId;
      let endpoint;
      
      if (question.isNotebookExample) {
        // For notebook examples, the ID might be in different formats:
        // - "notebook_xxx" (string with prefix)
        // - xxx (number or string without prefix)
        const rawId = question.id || question._id;
        if (!rawId) {
          throw new Error('Question ID is undefined');
        }
        
        // If it already has the prefix, use it directly; otherwise add the prefix
        if (typeof rawId === 'string' && rawId.startsWith('notebook_')) {
          questionId = rawId;
        } else {
          // Extract numeric part if it's a string like "notebook_541", or use the ID directly
          const numericId = typeof rawId === 'string' 
            ? rawId.replace('notebook_', '') 
            : String(rawId);
          questionId = `notebook_${numericId}`;
        }
        endpoint = `/teacher/questions/${questionId}/students`;
      } else {
        // For regular questions, use integer ID
        questionId = question._id || question.id;
        if (!questionId) {
          throw new Error('Question ID is undefined');
        }
        endpoint = `/teacher/questions/${questionId}/students`;
      }
      
      const response = await axios.get(endpoint, {
        params: {
          courseTemplateId: selectedCourse?.id
        }
      });
      setQuestionStudents(response.data);
    } catch (error) {
      console.error('Error fetching students for question:', error);
      setQuestionStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleDeleteQuestion = async (questionId, isNotebookExample = false) => {
    if (!window.confirm('确定要删除这道题目吗？删除后将无法恢复。')) {
      return;
    }

    try {
      if (isNotebookExample) {
        // Delete notebook example
        await axios.delete(`/notebook-examples/${questionId}`);
      } else {
        // Delete regular question
        await axios.delete(`/questions/${questionId}`);
      }
      alert('题目删除成功！');
      fetchQuestions();
      fetchOverviewStats();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('删除失败：' + (error.response?.data?.error || '请重试'));
    }
  };

  const handleSectionToggle = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleSettingsClick = () => {
    navigate('/teacher/settings');
  };

  const sidebarConfig = createTeacherSidebarConfig({
    navigate,
    user,
    selectedCourse,
    onLogout: handleLogout,
    currentPath: location.pathname,
    tabValue,
    setTabValue
  });
  const drawerContent = (
    <SidebarWithAccordion
      {...sidebarConfig}
      onSettingsClick={handleSettingsClick}
    />
  );

  // AppBar actions
  const appBarActions = null;

  // AppBar title
  const getAppBarTitle = () => {
    if (tabValue === 0) return '数据总览';
    if (tabValue === 1) return '学生管理';
    return '题目管理';
  };

  return (
    <>
      <DashboardLayout
        drawerContent={drawerContent}
        appBarTitle={getAppBarTitle()}
        appBarActions={appBarActions}
        courseName={selectedCourse?.name}
        courseTermCode={selectedCourse?.termCode}
        mobileOpen={mobileOpen}
        onDrawerToggle={handleDrawerToggle}
        onLogout={handleLogout}
      >
        {/* Overview Tab */}
        {tabValue === 0 && (
          <TeacherOverviewTab overviewStats={overviewStats} loading={loading} />
        )}

        {/* Students Tab */}
        {tabValue === 1 && (
          <TeacherStudentsTab
            students={students}
            loading={loading}
            onDeleteClick={handleDeleteClick}
          />
        )}

        {/* Questions Tab */}
        {tabValue === 2 && (
          <TeacherQuestionsTab
            questions={questions}
            categories={categories}
            selectedNotebook={selectedNotebook}
            notebooks={notebooks}
            loading={loading}
            expandedSections={expandedSections}
            onSectionToggle={handleSectionToggle}
            onViewStudents={handleViewQuestionStudents}
            onDelete={handleDeleteQuestion}
            sortBy={sortBy}
            onSortChange={setSortBy}
            selectedCourse={selectedCourse}
            onQuestionUpdate={(updated) => {
              setQuestions((prev) =>
                prev.map((q) =>
                  String(q.id ?? q._id) === String(updated.id ?? updated._id) ? { ...q, ...updated } : q
                )
              );
            }}
            onQuestionClick={(question, context) => {
              if (question.isNotebookExample) {
                const exampleId = question.id.replace('notebook_', '');
                navigate(`/notebook-exercise/${exampleId}`, { state: context ?? null });
              } else {
                navigate(`/question/${question._id || question.id}`, { state: context ?? null });
              }
            }}
          />
        )}
      </DashboardLayout>

      {/* Delete Student Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            确定要删除学生 <strong>{studentToDelete?.name}</strong> 吗？此操作不可恢复。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>取消</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? '删除中...' : '删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Question Students Dialog */}
      <Dialog open={studentsDialogOpen} onClose={() => setStudentsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          查看提交学生 - {selectedQuestion?.title}
        </DialogTitle>
        <DialogContent>
          {loadingStudents ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : questionStudents.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>暂无注册学生</Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {/* Statistics */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  统计信息
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      总人数
                    </Typography>
                    <Typography variant="h6">
                      {questionStudents.length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      提交人数
                    </Typography>
                    <Typography variant="h6">
                      {questionStudents.filter(s => s.submittedAt !== null && s.submittedAt !== undefined).length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      通过人数
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {questionStudents.filter(s => s.status === 'passed').length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" title="已提交但未通过">
                      未通过人数（已提交）
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {questionStudents.filter(s => s.submittedAt !== null && s.submittedAt !== undefined && s.status !== 'passed').length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      未提交人数
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {questionStudents.filter(s => s.submittedAt === null || s.submittedAt === undefined).length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      平均分
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {(() => {
                        const submittedStudents = questionStudents.filter(s => s.submittedAt !== null && s.submittedAt !== undefined && s.score !== null);
                        return submittedStudents.length > 0
                          ? (submittedStudents.reduce((sum, s) => sum + (s.score || 0), 0) / submittedStudents.length).toFixed(1)
                          : '0.0';
                      })()}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Students List */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>学生姓名</strong></TableCell>
                      <TableCell><strong>手机号</strong></TableCell>
                      <TableCell align="center"><strong>状态</strong></TableCell>
                      <TableCell align="center"><strong>分数</strong></TableCell>
                      <TableCell><strong>提交时间</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {questionStudents.map((student) => {
                      const hasSubmitted = student.submittedAt !== null && student.submittedAt !== undefined;
                      const isPassed = student.status === 'passed';
                      const isNotSubmitted = !hasSubmitted;
                      
                      return (
                        <TableRow key={student.userId || student.id}>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <UserAvatar 
                                user={student}
                                size={32}
                              />
                              <Typography>{student.name}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{student.phoneNumber || '-'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                isNotSubmitted ? '未提交' :
                                isPassed ? '通过' : 
                                '未通过'
                              }
                              size="small"
                              color={
                                isNotSubmitted ? 'warning' :
                                isPassed ? 'success' : 
                                'error'
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={
                                isNotSubmitted ? 'text.secondary' :
                                isPassed ? 'success.main' : 
                                'error.main'
                              }
                            >
                              {student.score !== undefined && student.score !== null ? student.score : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {hasSubmitted ? new Date(student.submittedAt).toLocaleString('zh-CN') : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentsDialogOpen(false)}>关闭</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>上传题目</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {uploadResult && (
              <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {uploadResult.message}
              </Alert>
            )}

            <TextField
              select
              fullWidth
              label="类别"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="Dictionary">Dictionary</MenuItem>
              <MenuItem value="List">List</MenuItem>
              <MenuItem value="Loop">Loop</MenuItem>
              <MenuItem value="StringMethod">StringMethod</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="题号"
              value={questionNumber}
              onChange={(e) => setQuestionNumber(e.target.value)}
              placeholder="例如: q1"
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <TextField
                select
                fullWidth
                label="难度"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <MenuItem value="easy">简单</MenuItem>
                <MenuItem value="medium">中等</MenuItem>
                <MenuItem value="hard">困难</MenuItem>
                <MenuItem value="exam">考试原题</MenuItem>
              </TextField>
            </Box>

            <Box>
              <input
                accept=".py"
                style={{ display: 'none' }}
                id="upload-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="upload-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  选择Python文件
                </Button>
              </label>
              {selectedFile && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  ✓ {selectedFile.name}
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={!selectedFile || !questionNumber || uploading}
          >
            {uploading ? '上传中...' : '上传'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TeacherDashboard;
